import { variant as buttonVariants } from "./buttonVariants";
import { colorState } from "../../Foundation";

/* eslint-disable max-lines */
// Style Parameters to be applied when we hover over a button
export type HoverStyleReturnType = {
    backgroundColor: string;
    color: string;
    border?: string;
    activeOutlineColor?: string;
};

// Size of button
export type size = "sm" | "lg";
const smallButtonHeight = 28;

// Style Parameters to be applied on button reuses the return types of hover
export interface StyleReturnType extends HoverStyleReturnType {
    height: number;
    maxWidth: number;
    minWidth: number | string;
    opacity: number;
    pointerEvents: string;
    activeBackckgroundColor?: string;
}

export const defaultStyle = {
    backgroundColor: "#000",
    color: "#FFF",
    height: 28,
    maxWidth: 120,
    minWidth: 95,
    opacity: 1,
    pointerEvents: "all"
};

const paginationButtonStyle = {
    height: 35,
    maxWidth: 35,
    minWidth: 35,
    opacity: 1,
    pointerEvents: "all"
};

// This returns general style for button as a dictionary
export const getStyle = (
    variant: buttonVariants,
    size: size | undefined,
    disabled: boolean | undefined
): StyleReturnType => {
    const additionalStyle = { ...defaultStyle };

    if (disabled === true) {
        additionalStyle.opacity = 0.6;
        additionalStyle.backgroundColor = "#7F818C";
        additionalStyle.color = "#CCCCCC";
    }

    if (size === "lg") {
        additionalStyle.height = 40;
    }

    if (size === "sm") {
        additionalStyle.height = 32;
    }

    const style: { [variant: string]: StyleReturnType } = {
        "active-pagination": {
            ...paginationButtonStyle,
            backgroundColor: colorState.surface.brand.primary,
            color: "#fff"
        },
        attention: {
            ...additionalStyle,
            activeOutlineColor: "#2A2D40",
            backgroundColor: "#3D708D"
        },
        "attention-small": {
            ...additionalStyle,
            backgroundColor: colorState.surface.brand.primary,
            height: smallButtonHeight
        },

        danger: {
            ...additionalStyle,
            backgroundColor: "#980E32"
        },
        "highlight-secondary": {
            ...additionalStyle,
            backgroundColor: "#E2FAF8",
            border: "#008080",
            color: "#008080"
        },
        light: {
            ...additionalStyle,
            backgroundColor: "#EFEFEF",
            color: "#000000"
        },
        "light-transparent": {
            ...additionalStyle,
            backgroundColor: "transparent",
            border: "white",
            color: "white",
            minWidth: 45
        },
        "outline-black": {
            ...additionalStyle,
            backgroundColor: "#FFFFFF",
            border: "#2A2D40",
            color: colorState.text.primary
        },
        "outline-danger": {
            ...additionalStyle,
            backgroundColor: "#FFFFFF",
            border: "#D12626",
            color: "#D12626"
        },
        "outline-primary": {
            ...additionalStyle,
            backgroundColor: "#FFFFFF",
            border: "#2A2D40",
            color: "#2A2D40"
        },
        "outline-secondary": {
            ...additionalStyle,
            backgroundColor: "#FFFFFF",
            border: "#008A8A",
            color: "#008080"
        },
        pagination: {
            ...paginationButtonStyle,
            backgroundColor: "#FFFFFF",
            border: "#4692A9",
            color: "#6D6E71"
        },
        primary: {
            ...additionalStyle,
            activeOutlineColor: "#980E32",
            backgroundColor: "#2A2D40"
        },
        quaternary: {
            ...additionalStyle,
            activeOutlineColor: "#2A2D40",
            backgroundColor: colorState.actions.default["default-bg"],
            border: "transparent",
            color: colorState.text.secondary,
            minWidth: "fit-content"
        },
        "selected-option": {
            ...additionalStyle,
            backgroundColor: colorState.surface.brand.primary
        },
        success: {
            ...additionalStyle,
            backgroundColor: "#328662"
        },
        tertiary: {
            ...additionalStyle,
            activeOutlineColor: "#2A2D40",
            backgroundColor: "transparent",
            color: "black",
            minWidth: "fit-content"
        },
        "text-button": {
            ...additionalStyle,
            activeBackckgroundColor: colorState.actions.secondary["secondary-bg-pressed"],
            activeOutlineColor: colorState.border.brand["primary-strong"],
            backgroundColor: "transparent",
            color: colorState.text.brand.secondary,
            minWidth: "auto"
        },
        "unselected-option": {
            ...additionalStyle,
            backgroundColor: "#fff",
            border: "#C3C3C3",
            color: "#7C7C7C"
        },
        warning: {
            ...additionalStyle,
            backgroundColor: "#B2621B"
        }
    };

    return style[variant];
};

// This returns hover style for button as a dictionary
export const getHoverStyle = (variant: buttonVariants): HoverStyleReturnType => {
    const textColor = {
        color: "#FFFFFF"
    };
    const style: { [variant: string]: HoverStyleReturnType } = {
        "active-pagination": {
            backgroundColor: colorState.surface.brand.primary,
            color: "#fff"
        },
        attention: {
            backgroundColor: "#3D708D",
            ...textColor
        },
        "attention-small": {
            backgroundColor: colorState.surface.brand.primary,
            border: "none",
            ...textColor
        },
        danger: {
            backgroundColor: "#980E32",
            ...textColor
        },
        "highlight-secondary": {
            backgroundColor: "#E2FAF8",
            color: "#008080"
        },
        light: {
            backgroundColor: "#EFEFEF",
            color: "#000000"
        },
        "light-transparent": {
            backgroundColor: "#4692A9",
            border: "transparent",
            color: "#FFF"
        },
        "outline-black": {
            backgroundColor: "#FFFFFF",
            border: "#4692A9",
            color: "#4692A9"
        },
        "outline-danger": {
            backgroundColor: "#FFFFFF",
            border: "#D12626",
            color: "#D12626"
        },
        "outline-primary": {
            ...textColor,
            backgroundColor: "#FFFFFF",
            border: "#4692A9",
            color: "#4692A9"
        },
        "outline-secondary": {
            backgroundColor: "#E2FAF8",
            color: "#008080"
        },
        pagination: {
            backgroundColor: "#E2FAF8",
            color: "#008080"
        },
        primary: {
            backgroundColor: "#2A2D40",
            color: "#4692A9"
        },
        quaternary: {
            backgroundColor: colorState.actions.default["default-bg-hover"],
            border: "transparent",
            color: colorState.text.secondary
        },
        "selected-option": {
            ...textColor,
            backgroundColor: "#4692A9"
        },
        success: {
            backgroundColor: "#328662",
            ...textColor
        },
        tertiary: {
            backgroundColor: "transparent",
            border: "none",
            color: "#374151"
        },
        "text-button": {
            backgroundColor: colorState.actions.secondary["secondary-bg-hover"],
            border: "none",
            color: colorState.text.brand.secondary
        },
        "unselected-option": {
            backgroundColor: "#fff",
            color: `${colorState.text.secondary}`
        },
        warning: {
            backgroundColor: "#B2621B",
            ...textColor
        }
    };

    return style[variant];
};
